import type {
  RunSummary,
  Schedule,
  ScheduleTarget,
  TargetCatalog,
  TargetServer,
} from '../types/scheduledCommands';
import { describeInterval } from './interval';
import { formatReleaseAt } from './comingSoon';

/**
 * The arithmetic behind the Scheduled Commands panel.
 *
 * Mr. Roboto is the only thing that fires a schedule, and it answers with each
 * schedule's next run time. Everything here is derived from that answer so the
 * page can say what will happen (which runs sit close together, which servers a
 * target reaches, what a set of choices will do) without asking the bot again.
 */

/** Runs closer than this are drawn as crowded, matching the spacing the bot aims for. */
export const CROWDED_GAP_SECONDS = 30;

export type ScheduleState = 'active' | 'paused' | 'ended' | 'not_started';

type StateInput = Pick<Schedule, 'enabled' | 'paused_reason' | 'starts_at' | 'ends_at'>;

/**
 * Where a schedule stands right now.
 *
 * An end date that has passed reads as ended even before the bot's next tick
 * flips the row, so the page never shows a schedule as running for the few
 * seconds in between.
 */
export function scheduleState(schedule: StateInput, nowSec: number): ScheduleState {
  if (schedule.paused_reason === 'ended') return 'ended';
  if (schedule.ends_at !== null && schedule.ends_at <= nowSec) return 'ended';
  if (!schedule.enabled) return 'paused';
  if (schedule.starts_at !== null && schedule.starts_at > nowSec) return 'not_started';
  return 'active';
}

/** The last run reached no server, or broke. A partial run is shown, but is not failing. */
export function isFailing(run: RunSummary | null): boolean {
  return run !== null && (run.status === 'failed' || run.status === 'error');
}

export interface Fire {
  id: number;
  /** Unix seconds. */
  at: number;
}

/**
 * Every run due between `fromSec` and `toSec`, in time order.
 *
 * Steps forward from the next run the bot reported, by whole intervals, so the
 * projection keeps the same phase the bot's scheduler keeps. Start and end
 * dates are honoured. `cap` bounds the work for a long window full of
 * one-minute schedules.
 */
export function projectFires(schedules: Schedule[], fromSec: number, toSec: number, nowSec: number, cap = 5000): Fire[] {
  const fires: Fire[] = [];
  for (const schedule of schedules) {
    const state = scheduleState(schedule, nowSec);
    if (state !== 'active' && state !== 'not_started') continue;
    if (schedule.next_run_at === null || schedule.interval_seconds <= 0) continue;
    const step = schedule.interval_seconds;
    let at = schedule.next_run_at;
    if (at < fromSec) at += Math.ceil((fromSec - at) / step) * step;
    for (; at <= toSec && fires.length < cap; at += step) {
      if (schedule.starts_at !== null && at < schedule.starts_at) continue;
      if (schedule.ends_at !== null && at >= schedule.ends_at) break;
      fires.push({ id: schedule.id, at });
    }
  }
  return fires.sort((a, b) => a.at - b.at || a.id - b.id);
}

export interface GapStats {
  /** Smallest gap between two consecutive runs, in seconds; null with fewer than two. */
  closest: number | null;
  average: number | null;
  /** Indexes into the fires array of runs within CROWDED_GAP_SECONDS of a neighbour. */
  crowded: Set<number>;
}

export function gapStats(fires: Fire[]): GapStats {
  const crowded = new Set<number>();
  if (fires.length < 2) return { closest: null, average: null, crowded };
  let closest = Infinity;
  let total = 0;
  for (let i = 1; i < fires.length; i += 1) {
    const gap = fires[i].at - fires[i - 1].at;
    total += gap;
    if (gap < closest) closest = gap;
    if (gap < CROWDED_GAP_SECONDS) {
      crowded.add(i - 1);
      crowded.add(i);
    }
  }
  return { closest, average: total / (fires.length - 1), crowded };
}

/** A stable identity for a target, for de-duplication and React keys. */
export function targetKey(target: ScheduleTarget): string {
  switch (target.type) {
    case 'all':
      return 'all';
    case 'cluster':
      return `cluster:${target.name.toLowerCase()}`;
    case 'server':
      return `server:${target.address.toLowerCase()}`;
  }
}

export function targetLabel(target: ScheduleTarget): string {
  switch (target.type) {
    case 'all':
      return 'All servers';
    case 'cluster':
      return target.name;
    case 'server':
      return target.address;
  }
}

export interface Reach {
  /** Every server the targets resolve to, once each. */
  servers: TargetServer[];
  online: number;
  /** Reported Minecraft versions, most common first. */
  versions: { version: string; count: number }[];
  /** Servers with no reported version (offline, or not reporting). */
  unknownVersions: number;
  /** Targets the catalog does not know, e.g. a server that has since been removed. */
  missing: ScheduleTarget[];
}

/**
 * What a set of targets reaches, according to the server list the bot
 * dispatches against. Clusters match case-insensitively, as the bot's
 * dispatcher does.
 */
export function reachOf(targets: ScheduleTarget[], catalog: TargetCatalog | null): Reach {
  const empty: Reach = { servers: [], online: 0, versions: [], unknownVersions: 0, missing: [] };
  if (!catalog) return { ...empty, missing: targets.filter((t) => t.type !== 'all') };

  const byAddress = new Map(catalog.servers.map((s) => [s.address.toLowerCase(), s]));
  const picked = new Map<string, TargetServer>();
  const missing: ScheduleTarget[] = [];

  for (const target of targets) {
    if (target.type === 'all') {
      for (const server of catalog.servers) picked.set(server.address.toLowerCase(), server);
      continue;
    }
    if (target.type === 'server') {
      const server = byAddress.get(target.address.toLowerCase());
      if (server) picked.set(server.address.toLowerCase(), server);
      else missing.push(target);
      continue;
    }
    const name = target.name.toLowerCase();
    const cluster = catalog.clusters.find((c) => c.name.toLowerCase() === name);
    const members = cluster
      ? cluster.servers.map((address) => byAddress.get(address.toLowerCase())).filter((s): s is TargetServer => !!s)
      : catalog.servers.filter((s) => s.cluster?.toLowerCase() === name);
    if (members.length === 0) missing.push(target);
    for (const server of members) picked.set(server.address.toLowerCase(), server);
  }

  const servers = [...picked.values()].sort((a, b) => a.address.localeCompare(b.address));
  const counts = new Map<string, number>();
  let unknownVersions = 0;
  for (const server of servers) {
    if (server.version) counts.set(server.version, (counts.get(server.version) ?? 0) + 1);
    else unknownVersions += 1;
  }
  const versions = [...counts.entries()]
    .map(([version, count]) => ({ version, count }))
    .sort((a, b) => b.count - a.count || a.version.localeCompare(b.version, undefined, { numeric: true }));

  return { servers, online: servers.filter((s) => s.online).length, versions, unknownVersions, missing };
}

/** One line of a command for a table cell: whitespace collapsed, cut at `max` characters. */
export function commandPreview(command: string, max = 140): string {
  const flat = command.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

export function scheduleDisplayName(schedule: Pick<Schedule, 'name' | 'command'>): string {
  return schedule.name?.trim() || commandPreview(schedule.command, 60);
}

export const isTellrawCommand = (command: string): boolean => /^\s*\/?tellraw\s/i.test(command);

/** Matches `@a` and `@a[...]`, the selector the bot swaps for a player's name. */
const EVERYONE_SELECTOR = /@a(?:\[[^\]]*\])?/g;

/**
 * The command a "send to player" run will send: every `@a` narrowed to one
 * name. Mr. Roboto does the real replacement; this only shows staff what is
 * about to go out, and refuses up front when there is nothing to narrow.
 */
export function narrowToPlayer(command: string, player: string): { command: string; replaced: boolean } {
  let replaced = false;
  const narrowed = command.replace(EVERYONE_SELECTOR, () => {
    replaced = true;
    return player;
  });
  return { command: narrowed, replaced };
}

export const PLAYER_NAME = /^[A-Za-z0-9_]{3,16}$/;

interface DescribeInput {
  interval_seconds: number;
  enabled: boolean;
  starts_at: number | null;
  ends_at: number | null;
}

/**
 * The sentence the editor's save bar shows: what saving will actually do.
 *
 * `servers` is null while the server list is loading, and the sentence then
 * leaves the count out rather than guessing.
 */
export function describeSchedule(input: DescribeInput, servers: number | null, nowSec: number): string {
  const where = servers === null ? '' : servers === 1 ? ' on 1 server' : ` on ${servers} servers`;
  const cadence = `${describeInterval(input.interval_seconds)}${where}`;
  const startsLater = input.starts_at !== null && input.starts_at > nowSec;
  const until = input.ends_at !== null ? ` until ${formatReleaseAt(input.ends_at)}` : '';

  if (input.ends_at !== null && input.ends_at <= nowSec) {
    return 'The end date has passed, so this will not run. Move or clear the end date to use it again.';
  }
  if (!input.enabled) {
    return `Paused. When resumed it runs ${cadence}${until}.`;
  }
  if (startsLater) {
    return `Starts ${formatReleaseAt(input.starts_at as number)}, then runs ${cadence}${until}.`;
  }
  return `Runs ${cadence}${until ? `, from now${until}` : ''}.`;
}
